import React from 'react';
import { Box, Typography, Avatar, Grid } from '@mui/material';
import { Palette, Check } from '@mui/icons-material';
import { MiniPanelProps, ExpandedPanelProps } from '../../../../types/steps';
import MiniPanel from './MiniPanel';
import ExpandedPanel from './ExpandedPanel';
import { MapStyle } from '../../../../types';
import { StyleInfo } from '../../constants';
import { setStyle, useStyle } from '../../../../state/map';
import { useAppDispatch } from '../../../../state/types';

// Popular styles for quick access in mini panel
const popularStyleKeys = [MapStyle.LIGHT, MapStyle.DARK, MapStyle.NATURAL, MapStyle.LOVE];

export const StyleMiniPanel: React.FC<MiniPanelProps> = ({ isActive, onExpand }) => {
  const currentStyle = useStyle();
  const dispatch = useAppDispatch();

  const handleStyleClick = (style: MapStyle, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent expanding the panel
    dispatch(setStyle(style));
  };

  return (
    <MiniPanel isActive={isActive} onExpand={onExpand}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Palette sx={{ color: 'primary.main' }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Quick styles
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {popularStyleKeys.map((style) => (
            <Avatar
              key={style}
              onClick={(e) => handleStyleClick(style, e)}
              sx={{
                width: 28,
                height: 28,
                bgcolor: StyleInfo[style].color,
                cursor: 'pointer',
                border: '2px solid',
                borderColor: currentStyle === style ? 'primary.main' : 'transparent',
                '&:hover': { border: '2px solid', borderColor: 'primary.main' },
                fontSize: '10px'
              }}
            >
              {currentStyle === style && <Check fontSize="small" />}
            </Avatar>
          ))}
        </Box>
      </Box>
    </MiniPanel>
  );
};

export const StyleExpandedPanel: React.FC<ExpandedPanelProps> = ({ 
  isActive, 
  onMinimize, 
  onClose 
}) => {
  const currentStyle = useStyle();
  const dispatch = useAppDispatch();

  const allStyles = Object.values(MapStyle).map((style) => ({
    value: style,
    label: StyleInfo[style].name,
    color: StyleInfo[style].color,
  }));

  const handleStyleClick = (style: MapStyle) => {
    dispatch(setStyle(style));
  };

  return (
    <ExpandedPanel 
      isActive={isActive} 
      onMinimize={onMinimize} 
      onClose={onClose}
      title="Map Styles"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Grid container spacing={2}>
          {allStyles.map((style) => (
            <Grid item xs={6} key={style.value}>
              <Box
                onClick={() => handleStyleClick(style.value)}
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  border: '2px solid',
                  borderColor: currentStyle === style.value ? 'primary.main' : 'divider',
                  bgcolor: currentStyle === style.value ? 'action.selected' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    transform: 'translateY(-2px)',
                    boxShadow: 2
                  }
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: 80,
                    borderRadius: '8px',
                    bgcolor: style.color,
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {currentStyle === style.value && (
                    <Check sx={{ color: 'white', fontSize: 32 }} />
                  )}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'center' }}>
                  {style.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </ExpandedPanel>
  );
};
